/**
 * LadiesCategoryPicker - 2-step category selection for Ladies Hub ad creation
 * Step 1: Select main category (Fashion, Beauty, etc.)
 * Step 2: Select subcategory with helper text and search
 */

import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Check, Search, X } from 'lucide-react';
import { 
  LadiesHubCategories, 
  getCategoryLabel, 
  buildCategoryPath,
  LadiesHubColors
} from '../hubs/ladies/LadiesHubPremiumConfig';
import { getSubcategoryIcon } from '../config/ladiesSubcategoryIcons';

const ORANGE = LadiesHubColors.primary;
const ORANGE_LIGHT = LadiesHubColors.chipBg;
const ORANGE_BORDER = LadiesHubColors.chipBorder;

export default function LadiesCategoryPicker({
  value,
  onChange,
  lang = 'en',
  isRTL = false,
  defaultMainCategory = null,
}) {
  const [step, setStep] = useState(defaultMainCategory ? 2 : 1);
  const [selectedMain, setSelectedMain] = useState(value?.mainId || defaultMainCategory || null);
  const [selectedSub, setSelectedSub] = useState(value?.subId || null);
  const [searchQuery, setSearchQuery] = useState('');

  const mainCategories = LadiesHubCategories.mainCategories.filter(m => !m.isVirtual);

  const subcategories = useMemo(() => {
    if (!selectedMain) return [];
    const main = mainCategories.find(m => m.id === selectedMain);
    return main?.subcategories || [];
  }, [selectedMain, mainCategories]);

  // Search matches title AND helper text in both languages
  const filteredSubcategories = useMemo(() => {
    if (!searchQuery.trim()) return subcategories;
    const q = searchQuery.toLowerCase();
    return subcategories.filter(sub => {
      const labelEn = (sub.label?.en || '').toLowerCase();
      const labelAr = (sub.label?.ar || '').toLowerCase();
      const helperEn = (sub.helper?.en || '').toLowerCase();
      const helperAr = (sub.helper?.ar || '').toLowerCase();
      return labelEn.includes(q) || labelAr.includes(q) || helperEn.includes(q) || helperAr.includes(q);
    });
  }, [subcategories, searchQuery]);

  const handleMainSelect = (mainId) => {
    setSelectedMain(mainId);
    setSelectedSub(null);
    setSearchQuery('');
    setStep(2);
  };

  const handleSubSelect = (subId) => {
    setSelectedSub(subId);
    const categoryPath = buildCategoryPath(selectedMain, subId);
    onChange({
      mainId: selectedMain,
      subId: subId,
      categoryPath: categoryPath,
      hub: 'ladies'
    });
  };

  const handleBack = () => {
    setStep(1);
    setSearchQuery('');
  };

  const handleClear = () => {
    setSelectedMain(null);
    setSelectedSub(null);
    setStep(1);
    onChange(null);
  };

  const getLabel = (item, field = 'label') => {
    if (!item) return '';
    if (item[field]) {
      return lang === 'ar' ? item[field].ar : item[field].en;
    }
    return lang === 'ar' ? item.ar : item.en;
  };

  const getHelper = (item) => {
    if (!item?.helper) return '';
    return lang === 'ar' ? item.helper.ar : item.helper.en;
  };

  return (
    <div
      className="ladies-category-picker"
      dir={isRTL ? 'rtl' : 'ltr'}
      data-testid="ladies-category-picker"
      style={{
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      {selectedMain && selectedSub && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 14px',
            marginBottom: 12,
            borderRadius: 12,
            background: ORANGE_LIGHT,
            border: `1px solid ${ORANGE_BORDER}`,
            boxSizing: 'border-box',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
            <Check size={16} color={ORANGE} style={{ flexShrink: 0 }} />
            <span style={{ fontSize: 14, fontWeight: 600, color: '#1C1917', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {getCategoryLabel(selectedMain, lang)} → {getLabel(subcategories.find(s => s.id === selectedSub))}
            </span>
          </div>
          <button
            onClick={handleClear}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: 'rgba(0,0,0,0.08)',
              border: 'none',
              cursor: 'pointer',
              color: '#78716C',
              flexShrink: 0,
            }}
            data-testid="clear-category"
          >
            <X size={14} />
          </button>
        </div>
      )}

      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div
            key="step1"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.2 }}
            style={{ width: '100%', boxSizing: 'border-box' }}
          >
            <div style={{ marginBottom: 12 }}>
              <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#1C1917' }}>
                {lang === 'ar' ? 'اختاري الفئة الرئيسية' : 'Select Main Category'}
              </h4>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#78716C' }}>
                {lang === 'ar' ? 'اختاري الفئة التي تناسب إعلانك' : 'Choose the category that fits your ad'}
              </p>
            </div>

            <div
              className="ladies-category-grid"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 12,
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              {mainCategories.map((main) => (
                <motion.button
                  key={main.id}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => handleMainSelect(main.id)}
                  className="ladies-category-card"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    minWidth: 0,
                    minHeight: 72,
                    padding: '14px 12px',
                    borderRadius: 18,
                    background: selectedMain === main.id ? ORANGE_LIGHT : '#FAFAF9',
                    border: selectedMain === main.id ? `2px solid ${ORANGE}` : '1px solid #E7E5E4',
                    cursor: 'pointer',
                    textAlign: isRTL ? 'right' : 'left',
                    boxSizing: 'border-box',
                    transition: 'all 0.2s ease',
                  }}
                  data-testid={`main-category-${main.id}`}
                >
                  <span style={{ fontSize: 22, flexShrink: 0 }}>{main.icon}</span>
                  <span style={{ 
                    fontSize: 14, 
                    fontWeight: 600, 
                    color: '#1C1917',
                    flex: 1,
                    minWidth: 0,
                    marginInlineStart: 10,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {getLabel(main)}
                  </span>
                  <span style={{ flexShrink: 0, color: '#A8A29E' }}>
                    {isRTL ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
                  </span>
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div
            key="step2"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
            style={{ width: '100%', boxSizing: 'border-box' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <button
                onClick={handleBack}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  borderRadius: 10,
                  background: '#F5F5F4',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#57534E',
                  flexShrink: 0,
                }}
                data-testid="back-to-main"
              >
                {isRTL ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
              </button>
              <div style={{ minWidth: 0, flex: 1 }}>
                <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#1C1917' }}>
                  {getCategoryLabel(selectedMain, lang)}
                </h4>
                <p style={{ margin: 0, fontSize: 12, color: '#78716C' }}>
                  {lang === 'ar' ? 'اختاري التصنيف الفرعي' : 'Select subcategory'}
                </p>
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 12px',
                marginBottom: 12,
                borderRadius: 10,
                background: '#F5F5F4',
                border: '1px solid #E7E5E4',
                boxSizing: 'border-box',
                width: '100%',
              }}
            >
              <Search size={16} color="#A8A29E" style={{ flexShrink: 0 }} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={lang === 'ar' ? 'ابحثي...' : 'Search subcategories...'}
                style={{
                  flex: 1,
                  border: 'none',
                  background: 'transparent',
                  outline: 'none',
                  fontSize: 14,
                  color: '#1C1917',
                  minWidth: 0,
                }}
                data-testid="subcategory-search"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#A8A29E',
                    padding: 0,
                    flexShrink: 0,
                  }}
                >
                  <X size={14} />
                </button>
              )}
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                maxHeight: 300,
                overflowY: 'auto',
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              {filteredSubcategories.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#A8A29E', padding: 20 }}>
                  {lang === 'ar' ? 'لا توجد نتائج' : 'No results found'}
                </p>
              ) : (
                filteredSubcategories.map((sub) => {
                  const isSelected = selectedSub === sub.id;
                  const SubIcon = getSubcategoryIcon(sub.id);
                  const helperText = getHelper(sub);
                  return (
                    <motion.button
                      key={sub.id}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleSubSelect(sub.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '12px 14px',
                        borderRadius: 12,
                        background: isSelected ? ORANGE_LIGHT : '#FAFAF9',
                        border: isSelected ? `2px solid ${ORANGE}` : '1px solid #E7E5E4',
                        cursor: 'pointer',
                        textAlign: isRTL ? 'right' : 'left',
                        width: '100%',
                        boxSizing: 'border-box',
                        minHeight: 56,
                      }}
                      data-testid={`subcategory-${sub.id}`}
                    >
                      <span style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        width: 34,
                        height: 34,
                        borderRadius: 10,
                        background: isSelected ? 'rgba(212,168,67,0.18)' : 'rgba(212,168,67,0.08)',
                        color: isSelected ? '#92710a' : '#b8930a',
                        transition: 'background 0.15s, color 0.15s',
                      }}>
                        <SubIcon size={17} strokeWidth={1.8} />
                      </span>
                      <span style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: 14, fontWeight: isSelected ? 600 : 500, color: '#1C1917', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {getLabel(sub)}
                        </span>
                        {helperText && (
                          <span style={{ fontSize: 12, color: isSelected ? '#b8930a' : '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.3 }}>
                            {helperText}
                          </span>
                        )}
                      </span>
                      {isSelected && (
                        <div
                          style={{
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            background: ORANGE,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <Check size={14} color="#fff" />
                        </div>
                      )}
                    </motion.button>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        .ladies-category-picker {
          font-family: inherit;
          width: 100%;
          max-width: 100%;
          overflow-x: hidden;
        }
        .ladies-category-picker * {
          box-sizing: border-box;
        }
        .ladies-category-picker button:focus {
          outline: 2px solid ${ORANGE};
          outline-offset: 2px;
        }
        .ladies-category-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
          width: 100%;
        }
        .ladies-category-card {
          width: 100%;
          min-height: 72px;
        }
        @media (max-width: 360px) {
          .ladies-category-grid {
            gap: 8px;
          }
          .ladies-category-card {
            padding: 12px 10px !important;
            min-height: 64px;
          }
        }
      `}</style>
    </div>
  );
}
